// src/components/common/Header.tsx

import React from "react";
import {
  TrendingUp,
  FileText,
  CheckCircle,
  Clock,
  RefreshCw,
} from "lucide-react";
import { LoanStats } from "../../types/loan.types";
import { COMPANY_COLORS } from "../../constants/theme.constants";
import { StatCard } from "./StatCard";
import { UserProfile } from "../../auth/UserProfile";
import { useAuth } from "../../services/authService";
import { COUNTRIES, getCountryLabel } from "../../utils/countries";

export type FilterType =
  | "all"
  | "created_today"
  | "pending_customer_docs"
  | "pending_signed_docs"
  | "pending_fulfilment"
  | "equity_uploaded_week";

interface HeaderProps {
  stats: LoanStats;
  selectedCountry: string;
  activeFilter?: FilterType;
  onCountryChange?: (country: string) => void;
  onFilterChange?: (filter: FilterType) => void;
}

export const Header: React.FC<HeaderProps> = ({
  stats,
  selectedCountry,
  activeFilter = "all",
  onCountryChange,
  onFilterChange,
}) => {
  const { data: authState } = useAuth();
  const isAdmin = authState?.authority?.roles?.includes("DASHBOARD_ADMIN");
  const countryLabel = getCountryLabel(selectedCountry);

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (onCountryChange) onCountryChange(event.target.value);
  };

  // Stat cards are display-only — no click-to-filter behaviour

  const total =
    (stats.todayCreated || 0) +
      (stats.todayPendingCustomerDocs || 0) +
      (stats.todayPendingSignedDocs || 0) +
      (stats.todayPendingFulfilment || 0) +
      (stats.equityUploadedThisWeek || 0) || 1;

  const pct = (n: number) => Math.round(((n || 0) / total) * 100);

  return (
    <div className="mb-4 w-full">
      {/* ── Top Bar ── */}
      <div
        className="flex items-center justify-between w-full px-6 py-4 mb-0"
        style={{ backgroundColor: COMPANY_COLORS.primary }}
      >
        <h1
          className="text-xl font-extrabold whitespace-nowrap tracking-tight"
          style={{ color: COMPANY_COLORS.secondary }}
        >
          Applications Tracking Dashboard
        </h1>

        <div className="flex items-center gap-3">
          {isAdmin && (
            <select
              value={selectedCountry}
              onChange={handleCountryChange}
              className="px-4 py-2 rounded-lg font-bold text-sm outline-none border-none cursor-pointer"
              style={{
                backgroundColor: COMPANY_COLORS.secondary,
                color: COMPANY_COLORS.primary,
              }}
            >
              <option value="ALL">All Countries</option>
              {COUNTRIES.map((country) => (
                <option key={country.value} value={country.value}>
                  {country.label}
                </option>
              ))}
            </select>
          )}

          <UserProfile />
        </div>
      </div>

      {/* ── Live sub-bar ── */}
      <div
        className="flex items-center justify-center gap-2 px-6 py-2 text-xs border-b"
        style={{
          background: "#FFFFFF",
          color: COMPANY_COLORS.textMuted,
          borderColor: COMPANY_COLORS.border,
        }}
      >
        <span
          className="rounded-full animate-pulse"
          style={{
            width: 6,
            height: 6,
            background: "#22c55e",
            display: "inline-block",
          }}
        />
        Real-time loan tracking and status monitoring — {selectedCountry === 'ALL' ? 'All countries' : countryLabel}
      </div>

      {/* ── Stat Cards ── */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-4 border-b"
        style={{
          background: "#FFFFFF",
          borderColor: COMPANY_COLORS.border,
        }}
      >
        <StatCard
          title="Completed Today"
          value={stats.todayCreated}
          icon={FileText}
          description="Loans completed today"
          accentColor="#1E88E5"
          iconBg="#EBF2FC"
          progressWidth={pct(stats.todayCreated)}
        />

        <StatCard
          title="Pending KYC Docs"
          value={stats.todayPendingCustomerDocs}
          icon={Clock}
          description="Awaiting KYC docs"
          accentColor="#FB8C00"
          iconBg="#FFF3E0"
          progressWidth={pct(stats.todayPendingCustomerDocs)}
        />

        <StatCard
          title="Pending Signed Docs"
          value={stats.todayPendingSignedDocs}
          icon={RefreshCw}
          description="Awaiting signatures"
          accentColor="#26A69A"
          iconBg="#E0F5EE"
          progressWidth={pct(stats.todayPendingSignedDocs)}
        />

        <StatCard
          title="Full Equity (Week)"
          value={stats.todayPendingFulfilment}
          icon={CheckCircle}
          description="Full uploads this week"
          accentColor="#43A047"
          iconBg="#E8F5E9"
          progressWidth={pct(stats.todayPendingFulfilment)}
        />

        <StatCard
          title="Partial Equity (Week)"
          value={stats.equityUploadedThisWeek}
          icon={TrendingUp}
          description="Partial uploads this week"
          accentColor={COMPANY_COLORS.secondary}
          iconBg={COMPANY_COLORS.goldPale}
          progressWidth={pct(stats.equityUploadedThisWeek)}
        />
      </div>
    </div>
  );
};